/**
 * Seeded pseudorandom number generator using Mulberry32 algorithm
 * Maintains state between calls for proper random sequences
 */
class SeededRandom {
    private state: number;

    constructor(seed: number) {
        this.state =
            seed < 1 ? Math.floor(seed * 0x100000000) : Math.floor(seed);
    }

    /**
     * Generates the next random number in the sequence
     */
    next(): number {
        let t = (this.state += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    }

    /**
     * Resets the generator to its initial seed
     */
    reset(seed?: number): void {
        if (seed !== undefined) {
            this.state =
                seed < 1 ? Math.floor(seed * 0x100000000) : Math.floor(seed);
        }
    }
}

/**
 * Shuffles an array using Fisher-Yates algorithm
 * @param array - The array to shuffle
 * @param seed - Optional seed for reproducible randomness (creates new generator)
 * @returns A new shuffled array
 */
export function shuffleArray<T>(array: T[], seed?: number): T[] {
    const shuffled = [...array];

    let random: () => number;

    if (seed !== undefined) {
        const generator = new SeededRandom(seed);
        random = () => generator.next();
    } else {
        random = Math.random;
    }

    let m = shuffled.length;
    let t: T;
    let i: number;

    // While there remain elements to shuffle…
    while (m) {
        // Pick a remaining element…
        i = Math.floor(random() * m--);

        // And swap it with the current element.
        t = shuffled[m];
        shuffled[m] = shuffled[i];
        shuffled[i] = t;
    }

    return shuffled;
}

/**
 * @param seed - The initial seed value
 * @returns A random generator instance
 */
export function createRandomGenerator(seed: number): SeededRandom {
    return new SeededRandom(seed);
}
